import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Anchor, Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export function PortsDialog({ open, onClose }) {
  const qc = useQueryClient();
  const { data: ports = [] } = useQuery({
    queryKey: ["ports"],
    queryFn: async () => (await api.get("/ports")).data,
  });
  const [nombre, setNombre] = useState("");
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ["ports"] });

  const add = async (e) => {
    e.preventDefault();
    if (!nombre.trim()) return;
    setBusy(true);
    try {
      await api.post("/ports", { nombre: nombre.trim() });
      toast.success("Puerto agregado");
      setNombre("");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editValue.trim()) return;
    setBusy(true);
    try {
      await api.put(`/ports/${editing}`, { nombre: editValue.trim() });
      toast.success("Puerto actualizado");
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar el puerto ${p.nombre}?`)) return;
    try {
      await api.delete(`/ports/${p.id}`);
      toast.success("Puerto eliminado");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="ports-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <Anchor className="w-5 h-5 text-cyan-600" /> Gestión de Puertos
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={add} className="flex gap-2">
          <Input value={nombre} onChange={(e) => setNombre(e.target.value)}
                 placeholder="Nombre del puerto (Ej. HIT)" data-testid="port-name-input" />
          <Button type="submit" disabled={busy} className="bg-navy hover:bg-navy-800 text-white shrink-0" data-testid="port-add-button">
            <Plus className="w-4 h-4" />
          </Button>
        </form>
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {ports.length === 0 && <p className="text-sm text-slate-400 text-center py-4" data-testid="ports-empty">Sin puertos registrados</p>}
          {ports.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2" data-testid={`port-row-${p.nombre}`}>
              {editing === p.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} className="h-8" data-testid={`port-edit-input-${p.nombre}`} />
                  <button type="button" onClick={saveEdit} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" data-testid={`port-edit-save-${p.nombre}`}><Check className="w-4 h-4" /></button>
                  <button type="button" onClick={() => setEditing(null)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <>
                  <span className="font-semibold text-navy text-sm">{p.nombre}</span>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => { setEditing(p.id); setEditValue(p.nombre); }}
                            className="p-1.5 text-slate-400 hover:text-cyan-600" data-testid={`port-edit-${p.nombre}`}><Pencil className="w-4 h-4" /></button>
                    <button type="button" onClick={() => remove(p)}
                            className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`port-delete-${p.nombre}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
